import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, queryString } from '../api';
import { ErrorNotice, Notice, SelectField, Spinner, formatMoney } from '../components/ui';
import type { Er2Preview, MembershipYearSummary } from '../types';

/**
 * The BDI eR2 return.
 *
 * The screen leads with what is uncertain rather than with the download
 * button, because the format is a guess. Somebody uploading this to a live
 * portal should have read the assumptions before they get to the file, not
 * after — and the warnings name the individual members who will look wrong,
 * so they can be fixed here rather than in a spreadsheet afterwards.
 */
export function ReturnsPage() {
  const [years, setYears] = useState<MembershipYearSummary[] | null>(null);
  const [yearId, setYearId] = useState('');
  const [includeUnsettled, setIncludeUnsettled] = useState(false);
  const [preview, setPreview] = useState<Er2Preview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get<{ years: MembershipYearSummary[] }>('/membership-years')
      .then((result) => {
        setYears(result.years);
        const current = result.years.find((y) => y.is_current) ?? result.years[0];
        if (current) setYearId(String(current.id));
      })
      .catch(setError);
  }, []);

  useEffect(() => {
    if (yearId === '') return;
    setLoading(true);
    setError(null);
    api
      .get<Er2Preview>(
        `/returns/er2${queryString({
          membership_year_id: yearId,
          include_unsettled: includeUnsettled ? '1' : '',
        })}`,
      )
      .then(setPreview)
      .catch((failure) => {
        setPreview(null);
        setError(failure);
      })
      .finally(() => setLoading(false));
  }, [yearId, includeUnsettled]);

  const downloadUrl = `/api/returns/er2/csv${queryString({
    membership_year_id: yearId,
    include_unsettled: includeUnsettled ? '1' : '',
  })}`;

  return (
    <>
      <p className="muted small">
        <Link to="/subscriptions">← Back to subscriptions</Link>
      </p>

      <div className="page-head">
        <h1>BDI eR2 return</h1>
      </div>

      <Notice kind="warning">
        <strong>This format has not been confirmed.</strong> The eR2 upload specification is not
        published: their e-return page describes the system and points at{' '}
        <a href="mailto:support@membershipservices.org.uk">support@membershipservices.org.uk</a>.
        This file is built to a template that is an educated guess. Check it against a real return
        before uploading it to the live portal.
      </Notice>

      <ErrorNotice error={error} />

      <div className="card form wide">
        <div className="grid">
          <SelectField label="Membership year" value={yearId} onChange={(e) => setYearId(e.target.value)}>
            {(years ?? []).map((year) => (
              <option key={year.id} value={year.id}>
                {year.year}
                {year.is_current ? ' (current)' : ''}
              </option>
            ))}
          </SelectField>
          <div className="field">
            <label htmlFor="include-unsettled">Who to include</label>
            <select
              id="include-unsettled"
              value={includeUnsettled ? 'all' : 'settled'}
              onChange={(e) => setIncludeUnsettled(e.target.value === 'all')}
            >
              <option value="settled">Only those who have paid or been waived</option>
              <option value="all">Everyone with a subscription, paid or not</option>
            </select>
            <p className="hint">
              Declaring somebody to BDI who has not paid insures them at the association's expense.
            </p>
          </div>
        </div>
      </div>

      {loading ? <Spinner label="Building the return" /> : null}

      {preview !== null ? (
        <>
          <div className="card">
            <dl className="details">
              <div className="detail">
                <dt>Members in the file</dt>
                <dd>{preview.counts.rows}</dd>
              </div>
              <div className="detail">
                <dt>Colonies declared</dt>
                <dd>{preview.counts.colonies}</dd>
              </div>
              <div className="detail">
                <dt>BDI premium total</dt>
                <dd>{formatMoney(preview.counts.premium_pence / 100)}</dd>
              </div>
            </dl>

            <a href={downloadUrl} className="button-link primary">
              Download iwbka-er2-{preview.year}.csv
            </a>
          </div>

          {preview.warnings.length > 0 ? (
            <div className="card">
              <h2>Worth checking before you upload</h2>
              {preview.warnings.map((warning) => (
                <details key={warning.kind} className="warning-block">
                  <summary>{warning.detail}</summary>
                  <ul className="permission-list">
                    {warning.members.map((member) => (
                      <li key={member}>{member}</li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          ) : null}

          <div className="card">
            <h2>What has been assumed</h2>
            <ul className="permission-list">
              {preview.assumptions.map((assumption) => (
                <li key={assumption}>{assumption}</li>
              ))}
            </ul>
          </div>

          <div className="card">
            <h2>The first few rows</h2>
            <div className="scroll-x">
              <table className="members">
                <thead>
                  <tr>
                    {preview.columns.map((column) => (
                      <th scope="col" key={column}>
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 10).map((row, index) => (
                    <tr key={index}>
                      {preview.columns.map((column) => (
                        <td key={column} data-label={column}>
                          {row[column] === '' ? <span className="muted">—</span> : row[column]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.rows.length > 10 ? (
              <p className="muted small">
                Showing 10 of {preview.rows.length}. The download has them all.
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}
